/**
 * Manual scroll position management for iOS Safari compatibility.
 *
 * iOS Safari doesn't properly support Vue Router's scrollBehavior when
 * combined with <transition> wrappers. This utility provides manual
 * scroll position storage and restoration that works across all browsers.
 */

// Store scroll positions keyed by route path + key
const scrollPositions = new Map();

// Maximum number of positions to store (prevents memory leaks)
const MAX_STORED_POSITIONS = 50;

/**
 * Generate a unique key for a route
 * Uses history state key if available (for back/forward navigation)
 * @param {Object} route - Vue Router route object
 * @returns {string}
 */
function getRouteKey(route) {
  // Use history state key for proper back/forward support
  const historyKey = window.history.state?.key;
  if (historyKey) {
    return `${route.path}::${historyKey}`;
  }
  return route.fullPath;
}

/**
 * Save current scroll position for a route
 * @param {Object} route - Vue Router route object
 */
export function saveScrollPosition(route) {
  if (!route) return;

  const key = getRouteKey(route);
  const position = {
    x: window.pageXOffset || document.documentElement.scrollLeft,
    y: window.pageYOffset || document.documentElement.scrollTop
  };

  scrollPositions.set(key, position);

  // Cleanup old entries if we exceed max
  if (scrollPositions.size > MAX_STORED_POSITIONS) {
    const firstKey = scrollPositions.keys().next().value;
    scrollPositions.delete(firstKey);
  }
}

/**
 * Get saved scroll position for a route
 * @param {Object} route - Vue Router route object
 * @returns {Object|null} Position object with x, y or null if not found
 */
export function getSavedScrollPosition(route) {
  if (!route) return null;

  const key = getRouteKey(route);
  return scrollPositions.get(key) || null;
}

/**
 * Restore scroll position for a route
 * @param {Object} route - Vue Router route object
 * @param {Object} options - Options object
 * @param {boolean} options.smooth - Use smooth scrolling (default: false)
 * @returns {boolean} True if position was restored, false otherwise
 */
export function restoreScrollPosition(route, options = {}) {
  const position = getSavedScrollPosition(route);

  if (position) {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      window.scrollTo({
        left: position.x,
        top: position.y,
        behavior: options.smooth ? 'smooth' : 'instant'
      });
    });
    return true;
  }
  return false;
}

/**
 * Scroll to top of page
 * @param {Object} options - Options object
 * @param {boolean} options.smooth - Use smooth scrolling (default: false)
 */
export function scrollToTop(options = {}) {
  requestAnimationFrame(() => {
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: options.smooth ? 'smooth' : 'instant'
    });
  });
}

/**
 * Scroll to a hash element
 * @param {string} hash - Hash selector (e.g., '#section-id')
 * @param {Object} options - Options object
 * @param {boolean} options.smooth - Use smooth scrolling (default: false)
 * @returns {boolean} True if element was found and scrolled to
 */
export function scrollToHash(hash, options = {}) {
  if (!hash) return false;

  try {
    const element = document.querySelector(hash);
    if (element) {
      requestAnimationFrame(() => {
        element.scrollIntoView({
          behavior: options.smooth ? 'smooth' : 'instant'
        });
      });
      return true;
    }
  } catch (e) {
    // Invalid selector, ignore
  }
  return false;
}

/**
 * Carry out the router's scroll intent (see scrollBehavior in router.js):
 * restore a back/forward position, go to a hash, or go to the top.
 * @param {Object|null} intent - From getPendingScrollIntent()
 */
export function applyScrollIntent(intent) {
  if (!intent || intent.type === 'none') return;

  switch (intent.type) {
    case 'restore':
      // Try to restore saved position, fallback to top
      if (!restoreScrollPosition(intent.route)) {
        scrollToTop();
      }
      break;
    case 'hash':
      // Scroll to hash element, fallback to top
      if (!scrollToHash(intent.hash)) {
        scrollToTop();
      }
      break;
    case 'top':
    default:
      scrollToTop();
      break;
  }
}

/**
 * Clear all stored scroll positions
 */
export function clearScrollPositions() {
  scrollPositions.clear();
}

/**
 * Check if this is a popstate navigation (back/forward)
 * @returns {boolean}
 */
export function isPopstateNavigation() {
  return window.history.state?.key !== undefined;
}

export default {
  applyScrollIntent,
  saveScrollPosition,
  getSavedScrollPosition,
  restoreScrollPosition,
  scrollToTop,
  scrollToHash,
  clearScrollPositions,
  isPopstateNavigation
};
